/**
 * Agent roles returned from auth.
 * `ROLE_PERMISSION_MATRIX` documents intended gates; `permissionsForRole` applies
 * arm/disarm RBAC when `environment.features.enforceArmDisarmRbac` is true.
 * When `useBackendPermissions` is true and backend permissions are provided,
 * those override the frontend matrix except for Admin, who always receives the
 * full matrix (UI is never the security boundary for mutate).
 */
import { environment } from '../../../../environments/environment';

export type AgentRole =
  | 'Admin'
  | 'Manager'
  | 'Supervisor'
  | 'Detection'
  | 'Escalation'
  | 'End-to-End';

export interface AgentPermissions {
  raiseToResolve: boolean;
  parkEvent: boolean;
  closeEvent: boolean;
  /** Armed Sites list → existing site disarm popover. */
  armDisarmSite: boolean;
  /** Event Details → Disarm tab → existing camera disarm popover. */
  armDisarmCamera: boolean;
  /** Add Snapshot control in playback hosts. */
  attachSnapshot: boolean;
  /** Attach alarm clip control in playback hosts. */
  attachAlarmClip: boolean;
  /** Browser tab bar "+" in EventQ AgentUI. */
  addBrowserTab: boolean;
  /** Queue, auto-process, and the rest of the operator dashboard. */
  accessEventsDashboard: boolean;
}

/**
 * Intended role → permission map.
 * Admin: every AgentUI capability, including Raise to Resolve.
 * Detection: raise + close + disarm site and camera; no park / attach media.
 * Escalation: event mutate stays in Resolve (close/raise/park). EventQ uses the same
 *   New Event tab and plus as other roles; that tab lists Armed and Disarmed Sites only.
 *   Selecting an armed site starts the site disarm popover. Camera disarm is allowed.
 *   Auto-process and the queue stay off.
 * Raise to Resolve is Detection and Admin in EventQ AgentUI.
 * End-to-End: park/close/add-tab/disarm site and camera yes; raise no.
 */
export const ROLE_PERMISSION_MATRIX: Record<AgentRole, AgentPermissions> = {
  Admin: {
    raiseToResolve: true,
    parkEvent: true,
    closeEvent: true,
    armDisarmSite: true,
    armDisarmCamera: true,
    attachSnapshot: true,
    attachAlarmClip: true,
    addBrowserTab: true,
    accessEventsDashboard: true,
  },
  Manager: {
    raiseToResolve: false,
    parkEvent: true,
    closeEvent: true,
    armDisarmSite: true,
    armDisarmCamera: true,
    attachSnapshot: true,
    attachAlarmClip: true,
    addBrowserTab: true,
    accessEventsDashboard: true,
  },
  Supervisor: {
    raiseToResolve: false,
    parkEvent: true,
    closeEvent: true,
    armDisarmSite: true,
    armDisarmCamera: true,
    attachSnapshot: true,
    attachAlarmClip: true,
    addBrowserTab: true,
    accessEventsDashboard: true,
  },
  Detection: {
    raiseToResolve: true,
    parkEvent: false,
    closeEvent: true,
    armDisarmSite: true,
    armDisarmCamera: true,
    attachSnapshot: false,
    attachAlarmClip: false,
    addBrowserTab: true,
    accessEventsDashboard: true,
  },
  Escalation: {
    raiseToResolve: false,
    parkEvent: false,
    closeEvent: false,
    armDisarmSite: true,
    armDisarmCamera: true,
    attachSnapshot: true,
    attachAlarmClip: true,
    addBrowserTab: true,
    accessEventsDashboard: false,
  },
  'End-to-End': {
    raiseToResolve: false,
    parkEvent: true,
    closeEvent: true,
    armDisarmSite: true,
    armDisarmCamera: true,
    attachSnapshot: true,
    attachAlarmClip: true,
    addBrowserTab: true,
    accessEventsDashboard: true,
  },
};

const DENY_ALL: AgentPermissions = {
  raiseToResolve: false,
  parkEvent: false,
  closeEvent: false,
  armDisarmSite: false,
  armDisarmCamera: false,
  attachSnapshot: false,
  attachAlarmClip: false,
  addBrowserTab: false,
  accessEventsDashboard: false,
};

/**
 * Resolves effective permissions for UI gating.
 * Admin always uses the frontend matrix (every capability).
 * For other roles, prefer backend-provided permissions when
 * `useBackendPermissions` is enabled. Otherwise apply raise/park/close/attach/add-tab
 * from the role matrix; apply `armDisarmSite` and `armDisarmCamera` from the matrix when
 * `enforceArmDisarmRbac` is enabled.
 */
export function permissionsForRole(
  role: AgentRole | null | undefined,
  backendPermissions?: AgentPermissions | null,
): AgentPermissions {
  if (role === 'Admin') {
    return permissionsFromMatrix('Admin');
  }

  if (environment.features.useBackendPermissions && backendPermissions) {
    return { ...backendPermissions };
  }

  if (!role || !isAgentRole(role)) {
    return { ...DENY_ALL };
  }

  return permissionsFromMatrix(role);
}

function permissionsFromMatrix(role: AgentRole): AgentPermissions {
  const matrix = ROLE_PERMISSION_MATRIX[role];
  const permissions: AgentPermissions = {
    raiseToResolve: matrix.raiseToResolve,
    parkEvent: matrix.parkEvent,
    closeEvent: matrix.closeEvent,
    armDisarmSite: matrix.armDisarmSite,
    armDisarmCamera: matrix.armDisarmCamera,
    attachSnapshot: matrix.attachSnapshot,
    attachAlarmClip: matrix.attachAlarmClip,
    addBrowserTab: matrix.addBrowserTab,
    accessEventsDashboard: matrix.accessEventsDashboard,
  };

  if (!environment.features.enforceArmDisarmRbac) {
    permissions.armDisarmSite = true;
    permissions.armDisarmCamera = true;
  }

  return permissions;
}

export function isAgentRole(value: unknown): value is AgentRole {
  return (
    value === 'Admin' ||
    value === 'Manager' ||
    value === 'Supervisor' ||
    value === 'Detection' ||
    value === 'Escalation' ||
    value === 'End-to-End'
  );
}
